import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Text } from "@mantine/core";
import { useAuthStore } from "@/lib/auth";
import { getConfig } from "@/lib/config";
import { applyPwaBranding } from "@/lib/pwa-branding";
import { brandingApi, tenantApi, emergencyApi, authApi } from "@/lib/api/client";
import { NetworkProvider } from "@/context/network-context";
import { NetworkStatusBanner } from "@/components/network-status-banner";
import { FloorAlarmBanner } from "@/components/floor-alarm-banner";
import { AreaBanner } from "@/components/guides/area-banner";
import { GuidePopup } from "@/components/guides/guide-popup";
import { GuideModalProvider } from "@/components/guides/guide-modal";
import { ActionIcon, Menu, Divider, Drawer, Stack, Tooltip, useMantineColorScheme, Group, Box, Flex, Indicator } from "@mantine/core";
import { LogOut, User, RefreshCw, Settings, Bell, Sun, Moon, Monitor, MoreHorizontal } from "lucide-react";
import { ChatPopover } from "@/components/chat-popover";
import { PageLoader } from "@/components/page-loader";
import { MaintenanceGate } from "@/components/maintenance-screen";
import { AnnouncementsHost } from "@/components/announcements-host";
import { IncomingCallBanner } from "@/components/doorbell/incoming-call-banner";
import { AnnouncementsIndicator } from "@/components/announcements-indicator";
import { PortalSocketProvider, usePortalEvent, RECONNECT_EVENT } from "@/lib/portal-socket";
import { useBookingApprovalCount } from "@/hooks/use-booking-approvals";
import { DismissedAnnouncementsProvider } from "@/lib/announcements-dismissal";
import { AnnouncementSoundProvider } from "@/lib/announcement-sound";

import LoginPage from "@/pages/login";
import SlackCallbackPage from "@/pages/slack-callback";
import OAuthCallbackPage from "@/pages/oauth-callback";
import PrivacyPage from "@/pages/privacy";
import TermsPage from "@/pages/terms";
import LinkPage from "@/pages/link";
import TenantSettingsPage from "@/pages/tenant-settings";
import SettingsAreaPage from "@/pages/settings-area";
import SlackInstallPage from "@/pages/slack-install";
import NotFoundPage from "@/pages/not-found";
import QrResolvePage from "@/pages/qr-resolve";
import DashboardPage from "@/pages/dashboard";
import AccountPage from "@/pages/account";
import ChangelogPage from "@/pages/changelog";
import ActivityPage from "@/pages/activity";
import ColleaguesPage from "@/pages/colleagues";
import MeetingRoomsPage from "@/pages/meeting-rooms";
import MeetingRoomsTimelinePage from "@/pages/meeting-rooms-timeline";
import FloorplanPage from "@/pages/floorplan";
import MeetingRoomDetailPage from "@/pages/meeting-room-detail";
import MyBookingsPage from "@/pages/my-bookings";
import PrintPage from "@/pages/print";
import InvoicesPage from "@/pages/invoices";
import NetworkLayout from "@/pages/network/layout";
import NetworkConnectTab from "@/pages/network/connect";
import NetworkDevicesTab from "@/pages/network/devices";
import NetworkPerformanceTab from "@/pages/network/performance";
import DoorbellPage from "@/pages/doorbell";
import DoorbellCallPage from "@/pages/doorbell-call";
import FAQPage from "@/pages/faq";
import SupportPage from "@/pages/support";
import SupportChatPage from "@/pages/support-chat";
import ContactPage from "@/pages/contact";
import NotificationSettingsPage from "@/pages/notification-settings";
import { useStaffView } from "@/lib/staff-view";
import { navItems, visibleNavItems } from "@/lib/nav";
import { loginReturnPath, rememberLoginReturn } from "@/lib/safe-redirect";
import { useSupportUnreadTotal } from "@/hooks/use-support-unread";

/** Guard a portal page: a signed-out member is sent to /login with the page
 *  they asked for — in the URL and remembered in storage — so a cold-loaded
 *  Slack call link still lands on the call after sign-in. Exported for the
 *  login-redirect regression test (`pages/slack-call-login-redirect.test.tsx`). */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    const params = new URLSearchParams(location.search);
    const authParam = params.get("auth");
    // Back to this page after login, query and hash included — a `/?guide=`
    // link still opens its guide (#436). Checked again on the way out.
    const from = loginReturnPath(location);
    // Remember it in storage too: a Slack link cold-loads in a new tab, where
    // router state never arrives, and the manual sign-in reads storage next.
    rememberLoginReturn(from);
    const loginUrl = authParam
      ? `/login?auth=${encodeURIComponent(authParam)}&redirect=${encodeURIComponent(from)}`
      : `/login?redirect=${encodeURIComponent(from)}`;
    return <Navigate to={loginUrl} replace state={{ from }} />;
  }

  return <>{children}</>;
}

function ThemeToggle() {
  const { setColorScheme, colorScheme } = useMantineColorScheme();
  return (
    <Menu>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Theme">
          {colorScheme === "dark" ? <Moon size={18} /> : colorScheme === "light" ? <Sun size={18} /> : <Monitor size={18} />}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<Sun size={14} />} onClick={() => setColorScheme("light")}>Light</Menu.Item>
        <Menu.Item leftSection={<Moon size={14} />} onClick={() => setColorScheme("dark")}>Dark</Menu.Item>
        <Menu.Item leftSection={<Monitor size={14} />} onClick={() => setColorScheme("auto")}>System</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** When an operator reassigns this member's tenant/site, the backend pushes a
 *  ``session_changed`` event to their portal.user.<id> group. Re-bootstrap the
 *  session into the new scope:
 *   1. refresh the JWT so its tenant_id/site_id claims follow the new tenant,
 *   2. refetch ``me`` and update the persisted auth store, so the identity
 *      (header / greeting) isn't a stale login-time snapshot,
 *   3. reload — the socket reconnects into the new groups and every query
 *      refetches with the fresh token + persisted user.
 *  If the member was removed from their tenant entirely (refresh/me 401), end
 *  the stale session instead. Mounted inside PortalSocketProvider so the
 *  subscription is live. */
function SessionReauth() {
  // Re-bootstrap into the (changed) scope: refresh the JWT, refetch ``me``, then
  // reload so the socket reconnects into the new groups and queries refetch. If
  // the member lost portal access (refresh/me fails), end the stale session.
  const reboot = useCallback(async () => {
    try {
      const refresh = localStorage.getItem("refresh_token");
      if (refresh) {
        const { data } = await authApi.refresh(refresh);
        localStorage.setItem("access_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
      }
      const me = await tenantApi.getMe();
      useAuthStore.getState().updateUser(me.data);
    } catch {
      useAuthStore.getState().logout();
    }
    window.location.reload();
  }, []);

  // The instant ``session_changed`` push is best-effort: an idle socket can drop
  // (proxy idle / half-open) and the push is lost with no replay — so on its own
  // a reassigned/removed member would keep a stale session indefinitely (#189).
  // Re-validate defensively: re-fetch ``me`` and re-bootstrap only on a CONFIRMED
  // change (tenant differs) or a definitive access loss (401/403). Transient
  // errors are ignored, so a blip can't log anyone out.
  const revalidate = useCallback(async () => {
    try {
      const me = await tenantApi.getMe();
      const current = useAuthStore.getState().user;
      if ((me.data?.tenant_id ?? null) !== (current?.tenant_id ?? null)) {
        await reboot();
        return;
      }
      // Same tenant: still refresh the persisted snapshot, so fields added
      // after this member's login (is_staff, beta status, name edits from
      // another device) reach the UI without a re-login.
      useAuthStore.getState().updateUser(me.data);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) await reboot();
    }
  }, [reboot]);

  // Instant path (when the push lands).
  usePortalEvent("session_changed", reboot);
  // Catch-up the moment a dropped socket reconnects (mirrors announcements-host).
  usePortalEvent(RECONNECT_EVENT, revalidate);
  // Safety net for the live-but-push-missed / half-open case the reconnect can't
  // catch: a slow poll bounds how long a removed member keeps a stale session.
  // The immediate call also refreshes the persisted user snapshot on app load.
  useEffect(() => {
    revalidate();
    const id = window.setInterval(revalidate, 30_000);
    return () => window.clearInterval(id);
  }, [revalidate]);

  return null;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthStore();
  // The next member to sign in on this browser must not see (or have the
  // login popup decide on) this session's cached data.
  const logOut = () => {
    logout();
    queryClient.clear();
  };
  const viewAsMember = useStaffView((s) => s.viewAsMember);
  const staffSeesAll = !!user?.is_staff && !viewAsMember;
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const visibleNav = visibleNavItems(navItems, user, staffSeesAll || !!user?.is_beta_tester);
  const primaryNav = visibleNav.filter((item) => item.primary);
  const overflowNav = visibleNav.filter((item) => !item.primary);

  const isNavActive = (to: string) =>
    to === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(to) ||
        (to === "/meeting-rooms" && location.pathname.startsWith("/my-bookings")) ||
        // Slack's install page is a step of Settings → Slack.
        (to === "/settings" && location.pathname.startsWith("/slack/"));
  const overflowActive = overflowNav.some((item) => isNavActive(item.to));

  const renderNavLink = ({ to, label, icon: Icon }: (typeof navItems)[number]) => {
    const isActive = isNavActive(to);
    return (
      <Link
        key={to}
        to={to}
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 2, padding: "4px 8px", fontSize: 10, borderRadius: 6,
          textDecoration: "none", transition: "color 0.15s",
          color: isActive ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-dimmed)",
          fontWeight: isActive ? 500 : 400,
        }}
      >
        {to === "/meeting-rooms" && approvalCount > 0 ? (
          <Indicator
            label={approvalCount > 99 ? "99+" : approvalCount}
            size={14}
            offset={-2}
            color="orange"
            aria-label={`${approvalCount} booking requests waiting for you`}
          >
            <Icon size={16} />
          </Indicator>
        ) : to === "/support" && supportUnread > 0 ? (
          <Indicator
            label={supportUnread > 99 ? "99+" : supportUnread}
            size={14}
            offset={-2}
            color="red"
            aria-label={`${supportUnread} unread`}
          >
            <Icon size={16} />
          </Indicator>
        ) : (
          <Icon size={16} />
        )}
        {label}
      </Link>
    );
  };
  const queryClient = useQueryClient();
  // Unread support/notification messages — badged on the Support nav item.
  // The header bell is announcements only; these are deliberately separate
  // so neither count hides the other.
  const supportUnread = useSupportUnreadTotal();
  const approvalCount = useBookingApprovalCount();

  const { data: branding } = useQuery({
    queryKey: ["branding"],
    queryFn: () => brandingApi.getBranding().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (branding?.site_name) {
      document.title = `${branding.site_name} Portal`;
    }
  }, [branding?.site_name]);

  // Installable portal: the site's manifest, Home Screen icon and title (static defaults until known).
  useEffect(() => {
    const apiBaseUrl = getConfig().API_BASE_URL;
    if (!apiBaseUrl || !branding?.site_id) return;
    applyPwaBranding(document, {
      apiBaseUrl,
      siteId: branding.site_id,
      siteName: branding.site_name ?? null,
      origin: window.location.origin,
    });
  }, [branding?.site_id, branding?.site_name]);

  useQuery({
    queryKey: ["faq"],
    queryFn: () => tenantApi.getFAQ().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  // Matching uses .find() + startsWith below, so the /network tab entries
  // MUST come before the bare "/network" (the Connect tab) — a "/network"
  // entry listed first would swallow its own sub-paths.
  const refreshableRoutes: Record<string, string[]> = {
    "/meeting-rooms": ["meeting-rooms", "meeting-rooms-schedule"],
    "/my-bookings": ["my-bookings", "my-booking-costs", "booking-approvals"],
    "/network/devices": ["network-devices"],
    "/network/performance": ["speedtest-history", "network-capacity"],
    "/network": ["wireless", "network-info", "network-plan"],
    "/faq": ["faq", "guides"],
    "/invoices": ["portal-invoices", "booking-costs"],
    "/activity": ["member-activity"],
  };
  const refreshKeys = Object.entries(refreshableRoutes).find(([path]) =>
    location.pathname === path || location.pathname.startsWith(path + "/")
  )?.[1];

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const handleRefresh = async () => {
    if (!refreshKeys) return;
    setIsRefreshing(true);
    await Promise.all(refreshKeys.map((k) => queryClient.invalidateQueries({ queryKey: [k] })));
    setIsRefreshing(false);
  };

  // Emergency banner — polls /tenant-portal/emergency/ every 30s and
  // shows a sticky red strip at the top when the site has an active
  // emergency. Mounted here so it appears on every authenticated page.
  // Critical-path safety feature; surface a degraded indicator when the
  // endpoint is failing so users don't silently miss real emergencies.
  const { data: emergencyData, failureCount: emergencyFailureCount, error: emergencyError } = useQuery({
    queryKey: ["emergency-state"],
    queryFn: () => emergencyApi.get().then((r) => r.data),
    refetchInterval: 30_000,
    staleTime: 0,
  });
  const emergencyDegraded = emergencyFailureCount >= 3;
  useEffect(() => {
    if (emergencyDegraded && emergencyError) {
      Sentry.captureException(emergencyError, {
        tags: { kind: "tenant-emergency-banner" },
      });
    }
  }, [emergencyDegraded, emergencyError]);

  return (
    <PortalSocketProvider>
    <DismissedAnnouncementsProvider>
    <AnnouncementSoundProvider>
    <NetworkProvider>
    <GuideModalProvider>
      <Flex direction="column" mih="100vh">
        <SessionReauth />
        <PageLoader />
        <AnnouncementsHost />
        <IncomingCallBanner />
        {emergencyDegraded && !emergencyData?.active && (
          <Box bg="orange.7" c="white" ta="center" fz="xs" py={4} px="md">
            <Text component="span" fz="xs">
              Couldn't verify emergency status — check with site staff if alarms are sounding.
            </Text>
          </Box>
        )}
        {/* Header */}
        <Box
          component="header"
          pos="sticky"
          top={0}
          style={{ zIndex: 40, borderBottom: "1px solid var(--mantine-color-default-border)", backdropFilter: "blur(8px)", backgroundColor: "var(--mantine-color-body)" }}
        >
          <Group h={60} justify="space-between" px="md" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              {branding?.logo_url && (
                <Link to="/" style={{ flexShrink: 0, display: "flex" }}>
                  <img
                    src={branding.logo_url}
                    alt={branding.tenant_name || user?.tenant_name || "Logo"}
                    style={{ height: 30, width: "auto", maxWidth: 120, objectFit: "contain" }}
                  />
                </Link>
              )}
              <Link to="/" style={{ textDecoration: "none", color: "inherit", minWidth: 0 }}>
                <Stack gap={0}>
                  <Text fw={700} fz="sm" lh={1.15} truncate>
                    {branding?.site_name || user?.site_name || "Member Portal"}
                  </Text>
                  {user?.tenant_name && (
                    <Text fz={11} c="dimmed" lh={1.15} truncate>
                      {user.tenant_name}
                    </Text>
                  )}
                </Stack>
              </Link>
            </Group>
            <Group gap={2} wrap="nowrap">
              <AnnouncementsIndicator />
              {refreshKeys && (
                <Tooltip label="Refresh" withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="lg"
                    onClick={handleRefresh}
                    loading={isRefreshing}
                    aria-label="Refresh"
                  >
                    <RefreshCw size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
              <ThemeToggle />
              <Menu>
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Account">
                    <User size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {user && (
                    <>
                      <Box px="sm" py={6} fz={12} c="dimmed">
                        {user.first_name ? `${user.first_name} ${user.last_name}`.trim() : user.email}
                      </Box>
                      <Divider />
                    </>
                  )}
                  <Menu.Item component={Link} to="/account" leftSection={<User size={14} />}>
                    Account
                  </Menu.Item>
                  <Menu.Item component={Link} to="/notifications" leftSection={<Bell size={14} />}>
                    Notifications
                  </Menu.Item>
                  {/* Every member has Settings areas (calendar feeds, Slack); admin-only ones are hidden there. */}
                  <Menu.Item component={Link} to="/settings" leftSection={<Settings size={14} />}>
                    Settings
                  </Menu.Item>
                  <Divider />
                  <Menu.Item color="red" onClick={logOut} leftSection={<LogOut size={14} />}>
                    Log out
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <ChatPopover currentPath={location.pathname} />
            </Group>
          </Group>
        </Box>

        <NetworkStatusBanner />
        <FloorAlarmBanner />
        <AreaBanner />
        <GuidePopup />

        {/* Main content */}
        <Box component="main" flex={1} pb={48}>
          {children}
        </Box>

        {/* Bottom navigation. The bar never wraps: on narrow screens the
            non-primary items collapse into a "More" sheet (which only exists
            when something actually overflows); wide screens fit everything. */}
        <Box
          component="nav"
          pos="fixed"
          bottom={0}
          left={0}
          right={0}
          style={{ zIndex: 40, borderTop: "1px solid var(--mantine-color-default-border)", backdropFilter: "blur(8px)", backgroundColor: "var(--mantine-color-body)" }}
        >
          <Group justify="space-around" gap={0} px="xs" py="xs" wrap="nowrap" visibleFrom="sm">
            {visibleNav.map((item) => renderNavLink(item))}
          </Group>
          <Group justify="space-around" gap={0} px="xs" py="xs" wrap="nowrap" hiddenFrom="sm">
            {primaryNav.map((item) => renderNavLink(item))}
            {overflowNav.length > 0 && (
              <Box
                component="button"
                onClick={() => setMoreOpen(true)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  gap: 2, padding: "4px 8px", fontSize: 10, borderRadius: 6,
                  border: "none", background: "none", cursor: "pointer", transition: "color 0.15s",
                  color: overflowActive ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-dimmed)",
                  fontWeight: overflowActive ? 500 : 400,
                }}
              >
                <MoreHorizontal size={16} />
                More
              </Box>
            )}
          </Group>
          <Drawer
            opened={moreOpen}
            onClose={() => setMoreOpen(false)}
            position="bottom"
            size="auto"
            title="More"
          >
            <Stack gap={4} pb="md">
              {overflowNav.map(({ to, label, icon: Icon }) => {
                const isActive = isNavActive(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMoreOpen(false)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "12px 8px", borderRadius: 8, textDecoration: "none",
                      color: isActive ? "var(--mantine-primary-color-filled)" : "var(--mantine-color-text)",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    <Icon size={20} />
                    {label}
                  </Link>
                );
              })}
            </Stack>
          </Drawer>
        </Box>
      </Flex>
    </GuideModalProvider>
    </NetworkProvider>
    </AnnouncementSoundProvider>
    </DismissedAnnouncementsProvider>
    </PortalSocketProvider>
  );
}

function App() {
  return (
    <Box mih="100vh">
      <MaintenanceGate>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
        <Route path="/slack-callback" element={<SlackCallbackPage />} />
        <Route path="/link" element={<LinkPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {/* Stable short link printed in QR codes (/q/<unique_id>). Protected:
            a signed-out member logs in first and comes back here (login reads
            the ProtectedRoute's `from`), then the backend picks the page. */}
        <Route
          path="/q/:uid"
          element={
            <ProtectedRoute>
              <QrResolvePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppShell>
                <DashboardPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AppShell>
                <AccountPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <AppShell>
                <NotificationSettingsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <AppShell>
                <ActivityPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/changelog"
          element={
            <ProtectedRoute>
              <AppShell>
                <ChangelogPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/colleagues"
          element={
            <ProtectedRoute>
              <AppShell>
                <ColleaguesPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* Layout route + index child (#310): the bare /network path IS the
            Connect tab — no redirect, no duplicate URL. /network/leases is
            still linked in the wild, so it lands on the Devices tab with the
            Static filter on rather than 404ing. */}
        <Route
          path="/network"
          element={
            <ProtectedRoute>
              <AppShell>
                <NetworkLayout />
              </AppShell>
            </ProtectedRoute>
          }
        >
          <Route index element={<NetworkConnectTab />} />
          <Route path="devices" element={<NetworkDevicesTab />} />
          <Route path="performance" element={<NetworkPerformanceTab />} />
          <Route path="leases" element={<Navigate to="/network/devices?show=static" replace />} />
        </Route>
        <Route
          path="/print"
          element={
            <ProtectedRoute>
              <AppShell>
                <PrintPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <AppShell>
                <InvoicesPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices/booking-costs"
          element={
            <ProtectedRoute>
              <AppShell>
                <InvoicesPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/doorbell"
          element={
            <ProtectedRoute>
              <AppShell>
                <DoorbellPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* A visitor call: from the ringing banner, a web push or Slack's
            "Answer call" link. Protected: a signed-out member logs in and
            comes back here (loginReturnPath). */}
        <Route
          path="/doorbell/call/:id"
          element={
            <ProtectedRoute>
              <AppShell>
                <DoorbellCallPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/slack/install"
          element={
            <ProtectedRoute>
              <AppShell>
                <SlackInstallPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppShell>
                <TenantSettingsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* One Settings area per page, like an Office Guide category (/faq/:categoryId). */}
        <Route
          path="/settings/:area"
          element={
            <ProtectedRoute>
              <AppShell>
                <SettingsAreaPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/faq"
          element={
            <ProtectedRoute>
              <AppShell>
                <FAQPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/faq/:categoryId"
          element={
            <ProtectedRoute>
              <AppShell>
                <FAQPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/meeting-rooms"
          element={
            <ProtectedRoute>
              <AppShell>
                <MeetingRoomsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/meeting-rooms/timeline"
          element={
            <ProtectedRoute>
              <AppShell>
                <MeetingRoomsTimelinePage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/meeting-rooms/:roomId"
          element={
            <ProtectedRoute>
              <AppShell>
                <MeetingRoomDetailPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/floorplan"
          element={
            <ProtectedRoute>
              <AppShell>
                <FloorplanPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <AppShell>
                <MyBookingsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-bookings/approval/:approvalId"
          element={
            <ProtectedRoute>
              <AppShell>
                <MyBookingsPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <AppShell>
                <SupportPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/new"
          element={
            <ProtectedRoute>
              <AppShell>
                <SupportChatPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support/:conversationId"
          element={
            <ProtectedRoute>
              <AppShell>
                <SupportChatPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact"
          element={
            <ProtectedRoute>
              <AppShell>
                <ContactPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contact/:conversationId"
          element={
            <ProtectedRoute>
              <AppShell>
                <ContactPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
        {/* Anything unmatched. React Router renders NOTHING when no route
            matches, so without this a stale bookmark — /wifi, /speedtest,
            /slack, all of which had redirects until recently — is a blank
            screen with no way out. Says what happened rather than silently
            redirecting, so a member who followed a dead link knows the link
            was dead instead of assuming the portal lost their page. */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <AppShell>
                <NotFoundPage />
              </AppShell>
            </ProtectedRoute>
          }
        />
      </Routes>
      </MaintenanceGate>
    </Box>
  );
}

export default App;
